/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Text,
  Space,
  Conditional,
  Button,
  Cross,
  Modal,
  ModalHeader,
  ModalContent,
  Dropdown,
  UploadFile,
  InputField,
  Textarea,
} from '@cashfree-intl/coherent';

import useClickOutside from 'utils/hooks';

import CustomIcon from 'components/CustomIcons';
import Formik from 'components/Formik';

import { ACCEPTED_SIGNATURE, XLS_SIGNATURE } from 'utils/constants';
import { validateFileType, previewFile } from 'utils/common';

import {
  addButtonTypeAttribute,
  isFileSelected,
  isFile,
  isText,
} from './utils';

import {
  DOCUMENT_STATUS_APPROVED,
  DROPDOWN,
  TEXT,
  TEXTAREA,
  SIZE_LIMIT,
} from '../constants';
import CombinedModesNote from './CombinedModesNote';

const UploadDocuments = ({
  closeModal,
  modeDetails,
  handleFormSubmit,
  downloadDocument,
}) => {
  const modalRef = useRef(null);
  useClickOutside(modalRef, closeModal);

  const { mode, documents, combinedModes = [] } = modeDetails;

  const { display, tdr: standardPricing } = mode;

  const fileFields = [];
  const textFields = {};
  const dropdownFields = {};

  const mandatoryFields = {};

  const initialValues = documents.reduce((acc, obj) => {
    if (isFile(obj.type)) {
      acc[obj.key] = { name: obj.value || '' };
      if (obj.docKey) acc[obj.key].docKey = obj.docKey;
      if (obj.value) acc[obj.key].fileSignature = obj.value.split('.').pop();
      fileFields.push(obj.key);
    } else if (isText(obj.type)) {
      acc[obj.key] = obj.value || '';
      textFields[obj.key] = obj.display;
      // if (obj.isApproved) isAnyTextFieldApproved = true;
    } else if (obj.type.toUpperCase() === DROPDOWN) {
      acc[obj.key] = obj.value || (obj.IsMultiSelect ? [] : '');
      dropdownFields[obj.key] = obj.display;
    }
    // handle the scenario for dropdown
    if (obj.isMandatory) mandatoryFields[obj.key] = true;
    return acc;
  }, {});

  const showPreviewForUploadedFile = fileData => () => {
    showPreview(fileData);
  };

  const showPreview = fileData => {
    if (fileData.file) {
      previewFile(URL.createObjectURL(fileData.file));
    } else if (fileData.docKey) {
      // fetch the signed url and then show the preview
      downloadDocument({
        data: { dockey: fileData.docKey },
        successCb: response => {
          if (response.docUrl) previewFile(response.docUrl);
        },
      });
    }
  };

  const validate = values => {
    const errors = {};
    fileFields.forEach(key => {
      if ('file' in values[key]) {
        // run the file validations
        const { file } = values[key];
        const fileSize = file.size / 1024 / 1024; // in MB
        if (fileSize > SIZE_LIMIT) {
          errors[key] = `Maximum file size allowed is ${SIZE_LIMIT} MB.`;
        }
      } else if ('error' in values[key]) {
        errors[key] = values[key].error;
      } else if (!('docKey' in values[key]) && mandatoryFields[key]) {
        errors[key] = 'Choose a file.';
      }
    });
    // validate text fields
    Object.keys(textFields).forEach(key => {
      if (!values[key] && mandatoryFields[key])
        errors[key] = `Enter ${textFields[key].toLowerCase()}.`;
    });
    // validate dropdown fields
    Object.keys(dropdownFields).forEach(key => {
      if (
        ((Array.isArray(values[key]) && values[key].length === 0) ||
          !values[key]) &&
        mandatoryFields[key]
      )
        errors[key] = `Select ${dropdownFields[key].toLowerCase()}.`;
    });
    return errors;
  };

  const onSubmit = (values, actions) => {
    const files = [];
    const regDetails = [];

    const approvedTextFields = {};

    const fieldsToBeUploaded = { files: [], textFields: [] };

    if (modeDetails.documents)
      modeDetails.documents.forEach(obj => {
        if (obj.type === 'text' && obj.isApproved)
          approvedTextFields[obj.key] = true;
      });

    Object.keys(values).forEach(key => {
      if (typeof values[key] === 'object' && 'file' in values[key]) {
        files.push({ docType: key, file: values[key].file });
        fieldsToBeUploaded.files.push(key);
      } else if (
        ['number', 'string'].includes(typeof values[key]) &&
        !approvedTextFields[key]
      ) {
        regDetails.push({ regKey: key, regVal: values[key] });
        fieldsToBeUploaded.textFields.push(key);
      }
      // add the dropdown fields as well here
    });

    handleFormSubmit({
      fieldsToBeUploaded,
      files,
      regDetails,
      modeDetails,
      showError: () => {
        actions.setSubmitting(false);
      },
    });
  };

  const renderUploadForm = () => (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
      validateOnBlur={false}
    >
      {({
        values,
        handleFieldBlur,
        handleFieldChange,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        ...formikProps
      }) => {
        const disableField =
          !formikProps.isValidating && formikProps.isSubmitting;

        const onFileInputChange = (key, type) => data => {
          const { file } = data;
          if (values[key] && !file) {
            setFieldValue(key, {});
          }
          // if (!file) {
          //   setTimeout(() => {
          //     addButtonTypeAttribute();
          //   }, 0);
          // }

          if (FileReader) {
            validateFileType(file, {
              signatures: { ...ACCEPTED_SIGNATURE, ...XLS_SIGNATURE },
            })
              .then(() => {
                setFieldValue(key, { file });
                if (errors[key]) setFieldError(key, '');
              })
              .catch(() => {
                setFieldError(
                  key,
                  `Only files with ${type} file format are allowed`,
                );
              });
          }

          if (!touched[key] && file) setFieldTouched(key, true);
        };

        const onUploadedFileResponse = (key, type) => data => {
          const { file } = data;
          if (!file) {
            setFieldValue(key, {});
          } else if (file && FileReader) {
            validateFileType(file, {
              signatures: { ...ACCEPTED_SIGNATURE, ...XLS_SIGNATURE },
            })
              .then(() => {
                setFieldValue(key, { file });
                if (errors[key]) setFieldError(key, '');
              })
              .catch(() => {
                setFieldError(
                  key,
                  `Only files with ${type} file format are allowed`,
                );
                setFieldValue(key, {
                  name: file.name,
                  error: `Only files with ${type} file format are allowed`,
                });
              });
          }
          if (!touched[key] && file) setFieldTouched(key, true);
        };

        // const onCrossClick = key => () => {
        //   setFieldError(key, '');
        //   setFieldValue(key, { name: '' });
        // };

        const onDropdownChange = key => (e, { value }) => {
          setFieldValue(key, value);
          setFieldTouched(key, true);
        };

        const getUploadedObj = obj => {
          const name =
            values[obj.key] && values[obj.key].file
              ? values[obj.key].file?.name
              : values[obj.key]?.name;

          if (name) return { name };
          return null;
        };

        return (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <Space direction="column" className="px-4 pt-4 pb-3 form-wrapper">
              <Space gap={2} direction="column" className="mb-3">
                <Text variant="b14">
                  To activate <span className="w-600">{display}</span> for your
                  for your account, we require you to submit the below
                  documents.
                </Text>
                {/* <p className="small-text text-light">
                File types supported are{' '}
                <span className="text-dark w-600">jpeg, jpg, png, pdf</span> and
                maximum file size allowed is{' '}
                <span className="text-dark w-600">2 MB.</span>
            </  p> */}
                <CombinedModesNote combinedModes={combinedModes} />
              </Space>
              <div>
                {documents.map(obj => (
                  <React.Fragment key={obj.key}>
                    <div
                      className={`document-grid ${
                        disableField ? 'disabled' : ''
                      }`}
                    >
                      <Conditional if={obj.display}>
                        <Space alignItems="center" className="display-wrapper">
                          <Text variant="b14">{obj.display}</Text>
                          <Conditional if={!obj.isMandatory}>
                            <Text
                              variant="b12"
                              color="bodyLight"
                              className="font-normal title-chip"
                            >
                              Optional
                            </Text>
                          </Conditional>
                        </Space>
                      </Conditional>
                      <div>
                        <Conditional if={isFile(obj.type)}>
                          <Conditional if={!isFileSelected(obj)}>
                            <UploadFile
                              type="secondary"
                              checkList={[
                                'Max file size: 2 MB',
                                `File type: ${obj.type.toLowerCase()}`,
                              ]}
                              sizeLimit={SIZE_LIMIT}
                              accept={`.${obj.type.toLowerCase()}`}
                              showPreview={showPreview}
                              onChange={onFileInputChange(
                                obj.key,
                                obj.type.toLowerCase(),
                              )}
                            />
                          </Conditional>
                          <Conditional if={isFileSelected(obj)}>
                            <div
                              className={
                                obj.docStatus?.trim().toUpperCase() ===
                                DOCUMENT_STATUS_APPROVED
                                  ? 'approved-file'
                                  : ''
                              }
                            >
                              <UploadFile
                                uploaded={getUploadedObj(obj)}
                                type="secondary"
                                editable={
                                  obj.docStatus?.trim().toUpperCase() !==
                                  DOCUMENT_STATUS_APPROVED
                                }
                                accept={`.${obj.type.toLowerCase()}`}
                                checkList={[
                                  'Max file size: 2 MB',
                                  `File type: ${obj.type.toLowerCase()}`,
                                ]}
                                showPreview={showPreviewForUploadedFile(obj)}
                                onChange={onUploadedFileResponse(
                                  obj.key,
                                  obj.type.toLowerCase(),
                                )}
                              />
                            </div>
                          </Conditional>
                        </Conditional>
                        <Conditional if={obj.type?.toUpperCase() === TEXT}>
                          <div
                            className={
                              obj.isApproved ? 'approved-text-field' : ''
                            }
                          >
                            <InputField
                              fluid
                              className="text-input-field"
                              id={obj.key}
                              name={obj.key}
                              label=""
                              value={values[obj.key]}
                              errors={errors}
                              touched={touched}
                              placeholder={`Enter ${obj.display}`}
                              onChange={handleFieldChange}
                              onBlur={handleFieldBlur}
                              disabled={disableField}
                            />
                          </div>
                        </Conditional>
                        {/* Add a condition for TEXTAREA */}
                        <Conditional if={obj.type?.toUpperCase() === TEXTAREA}>
                          <div
                            className={
                              obj.isApproved ? 'approved-text-field' : ''
                            }
                          >
                            <div>
                              {/* <Form.Field> */}
                              <Textarea
                                name={obj.key}
                                placeholder={`Enter ${obj.display}`}
                                className={`others-textarea ${
                                  errors[obj.key] ? 'error-border' : ''
                                }`}
                                value={values[obj.key]}
                                maxLength={200}
                                // placeholder=""
                                onChange={handleFieldChange}
                                onBlur={handleFieldBlur}
                              />
                              {/* </Form.Field> */}
                              <Text
                                variant="b12"
                                color="bodyLight"
                                className="mt-1"
                              >
                                Maximum 200 characters
                              </Text>
                            </div>
                          </div>
                        </Conditional>
                        <Conditional if={obj.type.toUpperCase() === DROPDOWN}>
                          <Conditional if={!obj.IsMultiSelect}>
                            <Dropdown
                              className={
                                obj.isApproved ? 'approved-dropdown' : ''
                              }
                              placeholder="Select the option"
                              options={
                                obj.dropDownValues?.map(option => ({
                                  key: option.value,
                                  text: option.display,
                                  value: option.value,
                                })) || []
                              }
                              value={values[obj.key]}
                              onChange={onDropdownChange(obj.key)}
                              selection
                              icon={<CustomIcon componentName="CHEVRONDOWN" />}
                            />
                          </Conditional>
                          <Conditional if={obj.IsMultiSelect}>
                            <Dropdown
                              className={
                                obj.isApproved ? 'approved-dropdown' : ''
                              }
                              multiple
                              placeholder="Select the option"
                              options={
                                obj.dropDownValues?.map(option => ({
                                  key: option.value,
                                  text: option.display,
                                  value: option.value,
                                })) || []
                              }
                              value={values[obj.key]}
                              onChange={onDropdownChange(obj.key)}
                              selection
                              icon={<CustomIcon componentName="CHEVRONDOWN" />}
                            />
                          </Conditional>
                        </Conditional>
                        <Conditional
                          if={
                            obj.docStatus?.trim().toUpperCase() ===
                            DOCUMENT_STATUS_APPROVED
                          }
                        >
                          <Space alignItems="center" className="verified-label">
                            <CustomIcon
                              componentName="GREEN_TICK_SMALL"
                              dimension="12"
                            />
                            <Text
                              variant="b12"
                              color="success"
                              className="font-normal"
                            >
                              Verified
                            </Text>
                          </Space>
                        </Conditional>
                      </div>
                    </div>
                    <div className="document-grid" key={obj.docType}>
                      <div />
                      <Conditional if={touched[obj.key] && errors[obj.key]}>
                        <Text variant="b12" color="danger" className="mt-1">
                          {errors[obj.key]}
                        </Text>
                      </Conditional>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </Space>
            <div className="px-4 pb-4 enable-payment-methods-footer">
              <Text className="mt-4" variant="b14" color="bodyLight">
                Activating <span className="text-dark w-600">{display}</span>{' '}
                payment mode usually takes a few days. A standard pricing of{' '}
                <span className="text-dark w-600">{standardPricing}</span> % is
                applicable per transaction.
              </Text>
              <div className="text-right cta-wrapper">
                <Button
                  type="button"
                  link
                  className="link"
                  onClick={closeModal}
                  disabled={disableField}
                  data-event-name="Form_UploadDocuments_Button_Cancel"
                >
                  Cancel
                </Button>
                <Button
                  primary
                  type="submit"
                  loading={disableField}
                  disabled={disableField}
                  data-event-name="Primary_Button_Submit"
                >
                  Submit
                </Button>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );

  useEffect(() => {
    setTimeout(() => {
      addButtonTypeAttribute();
    }, 0);
  }, [documents]);

  return (
    <Modal className="enable-payment-methods-modal" open ref={modalRef}>
      <ModalHeader>
        Enable {modeDetails.display}{' '}
        <Cross
          onClick={closeModal}
          data-event-name="Form_UploadDocuments_Icon_Cancel"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <ModalContent className="p-0">{renderUploadForm()}</ModalContent>
    </Modal>
  );
};

UploadDocuments.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modeDetails: PropTypes.object.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
};

export default UploadDocuments;
